import { useEffect, useState } from "react";

import Login from "./Login";
import Register from "./Register";
import ResetPassword from "./ResetPassword";
import Dashboard from "./Dashboard";
import SubjectSelection from "./SubjectSelection";
import Tutor from "./Tutor";
import questions from "./questions";

import {
  getSubjectPaper,
} from "./subjectCatalog";
import {
  insertTextAtSelection,
} from "./insertTextAtSelection";

import {
  getPresentedAIQuestion,
} from "./examQuestionParser";

import {
  formatAIQuestion,
} from "./questionHtmlFormatter";
import {
  getTutorDisplayName,
  hasActiveAISession,
} from "./aiModeRouting";

import {
  startAITutorSession,
  answerAITutorQuestion,
} from "./services/aiTutorService";
import { API_BASE_URL } from "./apiConfig";

const GUEST_MODE_ENABLED =
  process.env.REACT_APP_GUEST_MODE === "true";

const AI_MODE_OPTIONS = {
  "1": {
    mode: "areas",
    selectionType: "AREAS_FOR_IMPROVEMENT",
    selectedMode: "GUIDED_LEARNING",
    selectedDifficulty: "ADAPTIVE",
  },
  "2": {
    mode: "standard",
    selectionType: "STANDARD",
    selectedMode: "GUIDED_LEARNING",
    selectedDifficulty: "STANDARD",
  },
  "3": {
    mode: "challenging",
    selectionType: "CHALLENGING",
    selectedMode: "GUIDED_LEARNING",
    selectedDifficulty: "CHALLENGING",
  },
  exam: {
    mode: "exam",
    selectionType: "EXAM",
    selectedMode: "EXAM",
    selectedDifficulty: "EXAM",
  },
};

function getStoredToken() {
  return localStorage.getItem("chemMasterToken");
}

function getAuthHeaders(includeJson = false) {
  const headers = {
    Authorization: `Bearer ${getStoredToken()}`,
  };

  if (includeJson) {
    headers["Content-Type"] = "application/json";
  }

  return headers;
}

function getStartMenu(language) {
  return language === "af"
    ? [
      "Kies asseblief:",
      "",
      "1 = Gebiede vir verbetering",
      "2 = Standaard",
      "3 = Uitdagend",
      "OF tik 'Eksamen'",
    ].join("<br/>")
    : [
      "Please choose:",
      "",
      "1 = Areas for Improvement",
      "2 = Standard",
      "3 = Challenging",
      "OR type 'Exam'",
    ].join("<br/>");
}

function App() {
  const isResetPasswordPage =
    window.location.pathname === "/reset-password";

  const [input, setInput] = useState("");
  const [language, setLanguage] = useState("af");
  const [messages, setMessages] = useState([]);
  const [mode, setMode] = useState("");
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [attempts, setAttempts] = useState(0);
  const [score, setScore] = useState(0);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [showTutor, setShowTutor] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [weakTopics, setWeakTopics] = useState([]);
  const [isProcessingAnswer, setIsProcessingAnswer] =
    useState(false);
  const [results, setResults] = useState([]);
  const [aiSession, setAiSession] = useState(null);
  const [aiQuestionHistory, setAiQuestionHistory] =
    useState([]);
  const [isPaperComplete, setIsPaperComplete] =
    useState(false);
  const [selectedPaperCode, setSelectedPaperCode] =
    useState(null);

  useEffect(() => {
    const savedToken = getStoredToken();
    const savedUser =
      localStorage.getItem("chemMasterUser");

    const applySession = (data) => {
      localStorage.setItem(
        "chemMasterToken",
        data.token || savedToken
      );
      localStorage.setItem(
        "chemMasterUser",
        JSON.stringify(data.user)
      );
      setUserEmail(
        data.user.isGuest
          ? (data.user.preferredLanguage === "af"
            ? "EduVerse-leerder"
            : "EduVerse learner")
          : data.user.email
      );
      setLanguage(
        data.user.preferredLanguage || "af"
      );
      setIsLoggedIn(true);
      setIsAuthReady(true);
    };

    const startGuestSession = async () => {
      const response = await fetch(
        `${API_BASE_URL}/auth/guest-session`,
        { method: "POST" }
      );
      const data = await response.json();

      if (!response.ok || !data.token || !data.user) {
        throw new Error(
          data.message ||
          "Guest access could not be started."
        );
      }

      applySession(data);
    };

    const showLoginFallback = (error) => {
      console.error(
        "Automatic dashboard access failed:",
        error
      );
      localStorage.removeItem("chemMasterToken");
      localStorage.removeItem("chemMasterUser");
      setIsAuthReady(true);
    };

    if (!savedToken || !savedUser) {
      if (GUEST_MODE_ENABLED) {
        startGuestSession().catch(
          showLoginFallback
        );
      } else {
        setIsAuthReady(true);
      }
      return;
    }

    try {
      const tokenParts = savedToken.split(".");

      if (tokenParts.length !== 3) {
        throw new Error("Invalid token format");
      }

      const tokenPayload = JSON.parse(
        atob(
          tokenParts[1]
            .replace(/-/g, "+")
            .replace(/_/g, "/")
        )
      );

      const currentTime =
        Math.floor(Date.now() / 1000);

      if (
        !tokenPayload.exp ||
        tokenPayload.exp <= currentTime
      ) {
        localStorage.removeItem("chemMasterToken");
        localStorage.removeItem("chemMasterUser");
        if (GUEST_MODE_ENABLED) {
          startGuestSession().catch(
            showLoginFallback
          );
        } else {
          setIsAuthReady(true);
        }
        return;
      }

      const validateSavedSession = async () => {
        const response = await fetch(`${API_BASE_URL}/auth/session`, {
          headers: {
            Authorization: `Bearer ${savedToken}`,
          },
        });

        if (!response.ok) {
          throw new Error("Saved session is no longer authorised");
        }

        const data = await response.json();
        const user = data.user;
        applySession({ user });
      };

      validateSavedSession().catch((error) => {
        if (GUEST_MODE_ENABLED) {
          startGuestSession().catch(
            showLoginFallback
          );
        } else {
          showLoginFallback(error);
        }
      });
    } catch (error) {
      console.error(
        "Saved login could not be restored:",
        error
      );

      if (GUEST_MODE_ENABLED) {
        startGuestSession().catch(
          showLoginFallback
        );
      } else {
        showLoginFallback(error);
      }
    }
  }, []);

  const loadResultsFromDatabase = async () => {
    if (!getStoredToken()) {
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/results`,
        {
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(
          "Results could not be loaded:",
          data.message
        );
        return;
      }

      setResults(
        data.results.map((result) => ({
          resultId: result.result_id,
          marksAwarded: Number(
            result.total_marks_awarded
          ),
          marksAvailable: Number(
            result.total_marks_available
          ),
          percentage: Number(
            result.percentage
          ),
          completedAt: result.completed_at,
          paperTitle: result.paper_title,
          subjectCode: result.subject_code,
          paperCode: result.paper_code,
          paperNumber: Number(
            result.paper_number || 0
          ),
          selectionType: result.selection_type,
          sessionMode: result.session_mode,
          difficulty: result.difficulty,
        }))
      );
    } catch (error) {
      console.error(
        "Loading learner results failed:",
        error
      );
    }
  };

  const loadWeakTopicsFromDatabase = async (
    paperCode
  ) => {
    if (!getStoredToken() || !paperCode) {
      setWeakTopics([]);
      return;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/weak-topics?paperCode=${encodeURIComponent(
          paperCode
        )}`,
        {
          headers: getAuthHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(
          "Weak topics could not be loaded:",
          data.message
        );
        return;
      }

      setWeakTopics(
        data.topics.map((topic) => ({
          weakTopicId: topic.weak_topic_id,
          topicName: topic.topic_name,
          attemptsCount: Number(
            topic.attempts_count
          ),
          correctAnswersCount: Number(
            topic.correct_answers_count
          ),
          averagePercentage: Number(
            topic.average_percentage
          ),
          weaknessStatus:
            topic.weakness_status,
          lastPractisedAt:
            topic.last_practised_at,
        }))
      );
    } catch (error) {
      console.error(
        "Loading weak topics failed:",
        error
      );
    }
  };

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    loadResultsFromDatabase();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn || !selectedPaperCode) {
      setWeakTopics([]);
      return;
    }

    loadWeakTopicsFromDatabase(
      selectedPaperCode
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, selectedPaperCode]);

  const saveResultToDatabase = async (
    finalScore
  ) => {
    const totalMarksAvailable =
      questions.reduce(
        (total, question) =>
          total + question.marks,
        0
      );

    try {
      const response = await fetch(
        `${API_BASE_URL}/results`,
        {
          method: "POST",
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            totalMarksAvailable,
            totalMarksAwarded: finalScore,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(
          "Result could not be saved:",
          data.message
        );
        return;
      }

      await Promise.all([
        loadResultsFromDatabase(),
        selectedPaperCode
          ? loadWeakTopicsFromDatabase(
            selectedPaperCode
          )
          : Promise.resolve(),
      ]);
    } catch (error) {
      console.error(
        "Result saving request failed:",
        error
      );
    }
  };

  const saveTopicPerformance = async (
    topicName,
    isCorrect
  ) => {
    if (!getStoredToken()) {
      return false;
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/weak-topics`,
        {
          method: "POST",
          headers: getAuthHeaders(true),
          body: JSON.stringify({
            topicName,
            isCorrect,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        console.error(
          "Topic performance could not be saved:",
          data.message
        );
        return false;
      }

      return true;
    } catch (error) {
      console.error(
        "Saving topic performance failed:",
        error
      );
      return false;
    }
  };

  const beginAISession = async ({
    selectionType,
    selectedMode,
    selectedDifficulty,
  }) => {
    const selectedSubject =
      getSubjectPaper(selectedPaperCode);

    if (!selectedSubject) {
      throw new Error(
        "Please select a subject paper before starting the tutor."
      );
    }

    if (!selectedSubject.tutorAvailable) {
      throw new Error(
        language === "af"
          ? "Die tutor vir hierdie vraestel word nog voorberei."
          : "The tutor for this paper is still being prepared."
      );
    }

    const isExamSession =
      selectionType === "EXAM";

    const previousWeakTopics =
      weakTopics.map(
        (topic) => topic.topicName
      );

    const previousMistakeCounts =
      weakTopics.reduce(
        (counts, topic) => {
          counts[topic.topicName] =
            Math.max(
              topic.attemptsCount -
              topic.correctAnswersCount,
              0
            );

          return counts;
        },
        {}
      );

    const response =
      await startAITutorSession({
        gradeLevel: 12,
        subjectCode:
          selectedSubject.subjectCode,
        paperCode:
          selectedSubject.paperCode,
        paperNumber:
          selectedSubject.paperNumber,
        sessionLanguage: language,
        selectionType,
        mode: selectedMode,
        difficulty: selectedDifficulty,
        selectedTopics: [],
        targetMarks:
          isExamSession
            ? selectedSubject.exam.totalMarks
            : 50,
        testDurationMinutes:
          isExamSession
            ? selectedSubject.exam.durationMinutes
            : 60,
        calculatorAllowed: true,
        previousWeakTopics,
        previousMistakeCounts,
        questionHistory: [],
        examBlueprint:
          isExamSession
            ? {
              template_id:
                selectedSubject.exam.templateId,
              question_count:
                selectedSubject.exam.questionCount,
              total_marks:
                selectedSubject.exam.totalMarks,
              time_minutes:
                selectedSubject.exam.durationMinutes,
            }
            : {},
        curriculumReference: {},
      });

    setAiQuestionHistory([]);
    setAiSession(response);

    return response;
  };

  const submitAIAnswer = async (
    learnerAnswer
  ) => {
    if (
      !aiSession?.session_state ||
      !aiSession?.question
    ) {
      throw new Error(
        "The current AI session or question could not be found."
      );
    }

    const presentedQuestion =
      getPresentedAIQuestion(aiSession);

    const response =
      await answerAITutorQuestion({
        learnerAnswer,
        assessedAttemptNumber: 1,
        isFinalAttempt: true,
        currentSession:
          aiSession.session_state,
        currentQuestion:
          aiSession.question,
        currentSubquestionNumber:
          aiSession.session_state
            .current_subquestion_number ||
          null,
        questionHistory: [
          ...aiQuestionHistory.slice(-6),
          {
            question_number:
              presentedQuestion.question_number,
            question_text:
              presentedQuestion.question_text,
            learner_answer: learnerAnswer,
          },
        ],
      });

    const updatedAiSession = {
      ...response,
      session_state: {
        ...aiSession.session_state,
        ...response.session_state,
        session_id:
          response.session_state?.session_id ??
          aiSession.session_state?.session_id,
        paper_id:
          response.session_state?.paper_id ??
          aiSession.session_state?.paper_id,
      },
      question:
        response.next_action ===
          "finish_paper"
          ? null
          : response.question ??
          aiSession.question,
    };

    setAiSession(updatedAiSession);

    setAiQuestionHistory(
      (currentHistory) => [
        ...currentHistory,
        {
          question_number:
            presentedQuestion.question_number,
          learner_answer: learnerAnswer,
          tutor_response:
            response.learner_message,
          marking_status:
            response.marking?.status ||
            "pending",
          awarded_marks: Number(
            response.marking
              ?.awarded_marks || 0
          ),
        },
      ]
    );

    return updatedAiSession;
  };

  const startSelectedAIMode = async (
    option
  ) => {
    setAiSession(null);
    setAiQuestionHistory([]);
    setIsPaperComplete(false);
    setMode(option.mode);
    setCurrentQuestion(0);
    setAttempts(0);
    setScore(0);

    const session =
      await beginAISession(option);

    const formattedQuestion =
      formatAIQuestion(
        getPresentedAIQuestion(session),
        language
      );

    return (
      session.learner_message +
      (formattedQuestion
        ? `<br/><br/>${formattedQuestion}`
        : "")
    );
  };

  const handleAIAnswer = async (
    userMessage
  ) => {
    const response =
      await submitAIAnswer(userMessage);

    const previousQuestionNumber =
      getPresentedAIQuestion(
        aiSession
      )?.question_number;

    const newQuestionNumber =
      getPresentedAIQuestion(
        response
      )?.question_number;

    const movedToNewQuestion =
      response.next_action ===
      "next_question" ||
      (
        newQuestionNumber &&
        newQuestionNumber !==
        previousQuestionNumber &&
        response.next_action !==
        "finish_paper"
      );

    const learnerMessage =
      response.learner_message || "";

    setScore(
      Number(
        response.session_state
          ?.total_marks_earned || 0
      )
    );

    setCurrentQuestion(
      Number(
        response.session_state
          ?.questions_completed || 0
      )
    );

    setIsPaperComplete(
      response.next_action ===
      "finish_paper"
    );

    if (
      !movedToNewQuestion ||
      !response.question
    ) {
      return learnerMessage;
    }

    const formattedQuestion =
      formatAIQuestion(
        getPresentedAIQuestion(response),
        language
      );

    const questionAlreadyIncluded =
      getPresentedAIQuestion(response)
        ?.question_text &&
      learnerMessage.includes(
        getPresentedAIQuestion(response)
          .question_text
      );

    return (
      learnerMessage +
      (
        formattedQuestion &&
          !questionAlreadyIncluded
          ? `<br/><br/>${formattedQuestion}`
          : ""
      )
    );
  };

  const handleEasyModeAnswer = async (
    msg
  ) => {
    const currentQ =
      questions[currentQuestion];
    if (!currentQ) {
      return language === "af"
        ? "Geen vraag kon gevind word nie."
        : "No question could be found.";
    }

    if (msg === currentQ.answer) {
      await saveTopicPerformance(
        currentQ.topicName,
        true
      );

      const earnedMarks =
        attempts === 0
          ? currentQ.marks
          : Math.floor(
            currentQ.marks / 2
          );

      const newScore =
        score + earnedMarks;

      setScore(newScore);

      const nextQuestion =
        currentQuestion + 1;

      if (
        nextQuestion <
        questions.length
      ) {
        setCurrentQuestion(
          nextQuestion
        );
        setAttempts(0);

        return language === "af"
          ? `✅ Korrek! Jy kry ${earnedMarks}/${currentQ.marks}<br/><br/>${questions[nextQuestion].question}`
          : `✅ Correct! You earn ${earnedMarks}/${currentQ.marks}<br/><br/>${questions[nextQuestion].question}`;
      }

      await saveResultToDatabase(
        newScore
      );

      setIsPaperComplete(true);
      setAttempts(0);

      return language === "af"
        ? `🎉 Vraestel voltooi!<br/><br/>Totale punt: ${newScore}`
        : `🎉 Paper completed!<br/><br/>Total score: ${newScore}`;
    }

    if (attempts === 0) {
      setAttempts(1);

      return language === "af"
        ? "❌ Verkeerd. Probeer weer."
        : "❌ Incorrect. Try again.";
    }

    await saveTopicPerformance(
      currentQ.topicName,
      false
    );

    const nextQuestion =
      currentQuestion + 1;

    setAttempts(0);

    if (
      nextQuestion <
      questions.length
    ) {
      setCurrentQuestion(
        nextQuestion
      );

      return language === "af"
        ? `❌ Verkeerd. Die korrekte antwoord was ${currentQ.answer.toUpperCase()}<br/><br/>${questions[nextQuestion].question}`
        : `❌ Incorrect. The correct answer was ${currentQ.answer.toUpperCase()}<br/><br/>${questions[nextQuestion].question}`;
    }

    await saveResultToDatabase(score);
    setIsPaperComplete(true);

    return language === "af"
      ? `❌ Verkeerd. Die korrekte antwoord was ${currentQ.answer.toUpperCase()}<br/><br/>🎉 Vraestel voltooi!<br/>Totale punt: ${score}`
      : `❌ Incorrect. The correct answer was ${currentQ.answer.toUpperCase()}<br/><br/>🎉 Paper completed!<br/>Total score: ${score}`;
  };

  const handleSend = async () => {
    if (
      isProcessingAnswer ||
      input.trim() === ""
    ) {
      return;
    }

    setIsProcessingAnswer(true);

    const userMessage =
      input.trim();
    const msg =
      userMessage.toLowerCase();

    const newMessages = [
      ...messages,
      {
        sender: "user",
        text: userMessage,
      },
    ];

    let aiResponse =
      language === "af"
        ? "Ek verstaan nog nie 😅"
        : "I do not understand yet 😅";

    try {
      if (
        hasActiveAISession({
          mode,
          aiSession,
          isPaperComplete,
        })
      ) {
        aiResponse =
          await handleAIAnswer(
            userMessage
          );
      } else if (msg === "start") {
        aiResponse =
          getStartMenu(language);
      } else {
        const examKey =
          msg === "eksamen" ||
            msg === "exam"
            ? "exam"
            : null;

        const selectedOption =
          AI_MODE_OPTIONS[
          examKey || msg
          ];

        if (selectedOption) {
          aiResponse =
            await startSelectedAIMode(
              selectedOption
            );
        } else if (
          mode === "easy"
        ) {
          aiResponse =
            await handleEasyModeAnswer(
              msg
            );
        }
      }

    } catch (error) {
      console.error(
        "ChemMaster Tutor request failed:",
        error
      );

      const isIncompleteAnswer =
        error.message?.startsWith(
          "Answer all parts before sending."
        );

      if (isIncompleteAnswer) {
        const missingParts =
          error.message.replace(
            "Answer all parts before sending. Missing:",
            ""
          ).trim();

        aiResponse =
          language === "af"
            ? `Beantwoord asseblief al die dele voordat jy stuur. Ontbreek: ${missingParts}`
            : `Please answer all parts before sending. Missing: ${missingParts}`;
      } else {
        const tutorDisplayName =
          getTutorDisplayName(
            getSubjectPaper(
              selectedPaperCode
            )
          );

        aiResponse =
          language === "af"
            ? `${tutorDisplayName} kon nie nou antwoord nie. Probeer asseblief later weer.`
            : `${tutorDisplayName} could not respond right now. Please try again later.`;
      }
    }
    finally {
      setMessages([
        ...newMessages,
        {
          sender: "bot",
          text: aiResponse,
        },
      ]);

      setInput("");
      setIsProcessingAnswer(false);
    }
  };

  const insertSymbol = (
    symbol,
    selectionStart,
    selectionEnd
  ) => {
    setInput((previousInput) =>
      insertTextAtSelection(
        previousInput,
        symbol,
        selectionStart,
        selectionEnd
      )
    );
  };

  const handleLogout = () => {
    localStorage.removeItem(
      "chemMasterToken"
    );
    localStorage.removeItem(
      "chemMasterUser"
    );

    setIsLoggedIn(false);
    setUserEmail("");
    setShowTutor(false);
    setShowRegister(false);
    setSelectedPaperCode(null);
    setMessages([]);
    setAiSession(null);
    setAiQuestionHistory([]);
    setIsPaperComplete(false);
  };

  if (isResetPasswordPage) {
    return <ResetPassword />;
  }

  if (!isAuthReady) {
    return (
      <main className="auth-loading" aria-live="polite">
        {language === "af"
          ? "EduVerse word gelaai…"
          : "Loading EduVerse…"}
      </main>
    );
  }

  const selectedSubject =
    getSubjectPaper(selectedPaperCode);

  const selectedResults = selectedSubject
    ? results.filter(
      (result) =>
        result.paperCode ===
        selectedSubject.paperCode
    )
    : [];

  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#f3f6fb",
        fontFamily:
          "Arial, sans-serif",
      }}
    >
      {!isLoggedIn ? (
        showRegister ? (
          <Register
            language={language}
            onBackToLogin={() =>
              setShowRegister(false)
            }
          />
        ) : (
          <Login
            onLogin={(
              user,
              selectedLanguage
            ) => {
              setUserEmail(user.email);
              setLanguage(
                selectedLanguage ||
                user.preferredLanguage ||
                "af"
              );
              setIsLoggedIn(true);
            }}
            onOpenRegister={(
              selectedLanguage
            ) => {
              setLanguage(
                selectedLanguage
              );
              setShowRegister(true);
            }}
            onLanguageChange={
              setLanguage
            }
          />
        )
      ) : !selectedSubject ? (
        <SubjectSelection
          language={language}
          userEmail={userEmail}
          results={results}
          onSelect={(paperCode) => {
            setSelectedPaperCode(paperCode);
            setShowTutor(false);
            setMessages([]);
            setAiSession(null);
            setAiQuestionHistory([]);
            setIsPaperComplete(false);
          }}
          onLogout={
            GUEST_MODE_ENABLED
              ? null
              : handleLogout
          }
        />
      ) : !showTutor ? (
        <Dashboard
          language={language}
          userEmail={userEmail}
          selectedSubject={selectedSubject}
          onStartTutor={() =>
            setShowTutor(true)
          }
          onChangeSubject={() => {
            setSelectedPaperCode(null);
            setMessages([]);
            setAiSession(null);
            setAiQuestionHistory([]);
            setIsPaperComplete(false);
          }}
          onLogout={
            GUEST_MODE_ENABLED
              ? null
              : handleLogout
          }
          results={selectedResults}
          weakTopics={weakTopics}
        />
      ) : (
        <Tutor
          language={language}
          userEmail={userEmail}
          messages={messages}
          input={input}
          setInput={setInput}
          handleSend={handleSend}
          insertSymbol={insertSymbol}
          score={score}
          currentQuestion={
            currentQuestion
          }
          mode={mode}
          isProcessingAnswer={
            isProcessingAnswer
          }
          isPaperComplete={
            isPaperComplete
          }
          onBackToDashboard={() =>
            setShowTutor(false)
          }
          selectedSubject={selectedSubject}
        />
      )}
    </div>
  );
}

export default App;
